// Understory reviews.
// <understory-reviews>: on the homepage, reads the first N reviews from the real Judge.me markup on the
// product page (same origin, parsed with DOMParser into an inert document) and renders them as cards by
// cloning the server-rendered <template>. Reviews are shown in Judge.me's own order. Nothing is filtered,
// sorted or hand-picked. Fetched text is written only with textContent and setAttribute, never as markup.

const UNDERSTORY_REVIEWS_ROOT_MARGIN = '600px 0px';
const UNDERSTORY_REVIEWS_SELECTOR = '#judgeme_product_reviews .jdgm-ssr-reviews__list .jdgm-rev[data-review-id]';
const UNDERSTORY_REVIEWS_MAX_SCORE = 5;
const UNDERSTORY_REVIEWS_DEFAULT_LIMIT = 6;
const UNDERSTORY_REVIEWS_RESIZE_DELAY = 150;

let understoryReviewSequence = 0;

const understoryCollapse = (text) => (text || '').replace(/\s+/g, ' ').trim();

if (!customElements.get('understory-reviews')) {
  class UnderstoryReviews extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-reviews-rail]');
      this.list = this.querySelector('[data-reviews-list]');
      this.template = this.querySelector('template[data-review-template]');
      if (!this.rail || !this.list || !this.template) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: UNDERSTORY_REVIEWS_ROOT_MARGIN }
        );
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);
    }

    // Same-origin only. Section Rendering first (when an id is configured), then the full product page.
    async load() {
      try {
        const target = new URL(this.dataset.url || '', window.location.href);
        if (target.protocol !== window.location.protocol || target.host !== window.location.host) {
          this.dataset.state = 'empty';
          return;
        }

        const candidates = [];
        const sectionId = (this.dataset.sectionId || '').trim();
        if (sectionId) candidates.push(target.pathname + '?section_id=' + encodeURIComponent(sectionId));
        candidates.push(target.pathname);

        for (const candidate of candidates) {
          const response = await fetch(candidate, { credentials: 'same-origin' });
          if (!response.ok) continue;

          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const nodes = doc.querySelectorAll(UNDERSTORY_REVIEWS_SELECTOR);
          if (nodes.length === 0) continue;

          this.render(nodes);
          return;
        }

        this.dataset.state = 'empty';
      } catch (e) {
        console.error(e);
        this.dataset.state = 'empty';
      }
    }

    render(nodes) {
      const parsedLimit = parseInt(this.dataset.limit, 10);
      const limit = Math.min(Math.max(Number.isNaN(parsedLimit) ? UNDERSTORY_REVIEWS_DEFAULT_LIMIT : parsedLimit, 1), 12);
      // Merchant setting: 0 shows every review; 5 features only 5-star ones. Every review stays published on
      // the product page, and the summary above the cards always shows the true average and count.
      const parsedMinRating = parseFloat(this.dataset.minRating);
      const minRating = Number.isNaN(parsedMinRating) ? 0 : parsedMinRating;
      const seen = new Set();
      const fragment = document.createDocumentFragment();
      let rendered = 0;

      for (const node of nodes) {
        if (rendered >= limit) break;

        const reviewId = node.getAttribute('data-review-id');
        if (seen.has(reviewId)) continue;
        seen.add(reviewId);

        if (minRating > 0 && this.ratingOf(node) < minRating) continue;

        fragment.appendChild(this.buildCard(node));
        rendered += 1;
      }

      this.list.appendChild(fragment);
      this.rail.hidden = false;
      this.dataset.state = 'loaded';

      window.requestAnimationFrame(() => this.updateToggles());
      this.onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.updateToggles(), UNDERSTORY_REVIEWS_RESIZE_DELAY);
      };
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    ratingOf(node) {
      const ratingEl = node.querySelector('.jdgm-rev__rating');
      const parsed = parseFloat(ratingEl ? ratingEl.getAttribute('data-score') : '');
      return Math.min(Math.max(Number.isNaN(parsed) ? 0 : parsed, 0), UNDERSTORY_REVIEWS_MAX_SCORE);
    }

    buildCard(node) {
      const card = this.template.content.firstElementChild.cloneNode(true);
      const slot = (name) => card.querySelector('[data-slot="' + name + '"]');

      // Stars: the fill and the accessible name both come from the review's own rating.
      const rating = this.ratingOf(node);
      const starsSlot = slot('stars');
      if (starsSlot) {
        const graphic = starsSlot.querySelector('.understory-stars');
        if (graphic) graphic.style.setProperty('--understory-stars-fill', (rating / UNDERSTORY_REVIEWS_MAX_SCORE) * 100 + '%');
        starsSlot.setAttribute('aria-label', (starsSlot.getAttribute('data-label') || '').replace('__RATING__', String(rating)));
        starsSlot.removeAttribute('data-label');
      }

      // Title (often empty on Judge.me).
      const titleSlot = slot('title');
      const titleEl = node.querySelector('.jdgm-rev__title');
      const titleText = titleEl ? titleEl.textContent.trim() : '';
      if (titleSlot && titleText) {
        titleSlot.textContent = titleText;
        titleSlot.hidden = false;
      }

      // Body: one blank line between the review's paragraphs.
      const bodySlot = slot('body');
      const toggleSlot = slot('toggle');
      const bodyEl = node.querySelector('.jdgm-rev__body');
      let bodyText = '';
      if (bodyEl) {
        const paragraphs = Array.from(bodyEl.querySelectorAll(':scope > p'), (p) => understoryCollapse(p.textContent));
        bodyText = paragraphs.length ? paragraphs.join('\n\n') : understoryCollapse(bodyEl.textContent);
      }
      if (bodySlot && bodyText) {
        bodySlot.textContent = bodyText;
        if (toggleSlot) {
          understoryReviewSequence += 1;
          const bodyId = 'UnderstoryReviewBody-' + understoryReviewSequence;
          bodySlot.id = bodyId;
          toggleSlot.setAttribute('aria-controls', bodyId);
          toggleSlot.addEventListener('click', () => this.toggleBody(bodySlot, toggleSlot));
        }
      } else {
        if (bodySlot) bodySlot.remove();
        if (toggleSlot) toggleSlot.remove();
      }

      // Author, with the same name-format hiding Judge.me applies on the product page.
      const authorSlot = slot('author');
      const authorEl = node.querySelector('.jdgm-rev__author');
      if (authorSlot && authorEl) {
        const authorCopy = authorEl.cloneNode(true);
        authorCopy.querySelectorAll('.jdgm-author-all-initials, .jdgm-author-last-initial').forEach((el) => el.remove());
        authorSlot.textContent = understoryCollapse(authorCopy.textContent);
      }

      // Verified buyer.
      const verifiedSlot = slot('verified');
      if (verifiedSlot && node.getAttribute('data-verified-buyer') === 'true') verifiedSlot.hidden = false;

      // Date, in Judge.me's configured MM/DD/YYYY format.
      const dateSlot = slot('date');
      const timeEl = node.querySelector('time.jdgm-rev__timestamp');
      const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(timeEl ? timeEl.getAttribute('datetime') || '' : '');
      if (dateSlot && dateMatch) {
        dateSlot.setAttribute('datetime', dateMatch[1] + '-' + dateMatch[2] + '-' + dateMatch[3]);
        dateSlot.textContent = dateMatch[2] + '/' + dateMatch[3] + '/' + dateMatch[1];
      } else if (dateSlot) {
        dateSlot.remove();
      }

      return card;
    }

    toggleBody(body, toggle) {
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(expanded));
      if (expanded) {
        body.setAttribute('data-expanded', '');
      } else {
        body.removeAttribute('data-expanded');
      }
      const more = toggle.querySelector('[data-label-more]');
      const less = toggle.querySelector('[data-label-less]');
      if (more) more.hidden = expanded;
      if (less) less.hidden = !expanded;
      this.updateToggles();
    }

    // Show "Read more" only on cards whose body is actually clamped.
    updateToggles() {
      this.querySelectorAll('.understory-review').forEach((card) => {
        const body = card.querySelector('[data-slot="body"]');
        const toggle = card.querySelector('[data-slot="toggle"]');
        if (!body || !toggle) return;
        if (body.hasAttribute('data-expanded')) {
          toggle.hidden = false;
          return;
        }
        toggle.hidden = !(body.scrollHeight - body.clientHeight > 1);
      });
    }
  }

  customElements.define('understory-reviews', UnderstoryReviews);
}

// <understory-review-summary>: the star summary link on the product page. With JS it scrolls to the Judge.me
// reviews (smoothly, or instantly for visitors who asked for less motion) and moves focus to the reviews
// heading. Without JS, or when the target is not on the page, the native anchor behavior applies.
const UNDERSTORY_REVIEWS_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

if (!customElements.get('understory-review-summary')) {
  class UnderstoryReviewSummary extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('a[href^="#"]');
      if (!this.link) return;
      this.onClick = (event) => this.jump(event);
      this.link.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.link && this.onClick) this.link.removeEventListener('click', this.onClick);
    }

    jump(event) {
      const hash = this.link.getAttribute('href').slice(1);
      let id = hash;
      try {
        id = decodeURIComponent(hash);
      } catch (e) {
        id = hash;
      }

      // Missing target (for example inside a quick-add modal): leave the native behavior alone.
      const target = document.getElementById(id);
      if (!target) return;

      event.preventDefault();
      const reduced = window.matchMedia(UNDERSTORY_REVIEWS_REDUCED_MOTION).matches;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

      const focusTarget = target.querySelector('.jdgm-rev-widg__title') || target.querySelector('h2, h3') || target;
      if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }
  }

  customElements.define('understory-review-summary', UnderstoryReviewSummary);
}
